import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getChatSessions, deleteChatSession } from '../api';
import './ChatHistory.css';

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 3.5h9M5.5 3.5V2a1 1 0 011-1h1a1 1 0 011 1v1.5M3.5 3.5l.5 8.5a1 1 0 001 1h4a1 1 0 001-1l.5-8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const bucketFor = (dateStr) => {
  const date = startOfDay(dateStr);
  const today = startOfDay(new Date());
  const diffDays = Math.round((today - date) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return 'Previous 7 Days';
  if (diffDays <= 30) return 'Previous 30 Days';
  return 'Older';
};

const BUCKET_ORDER = ['Today', 'Yesterday', 'Previous 7 Days', 'Previous 30 Days', 'Older'];

/**
 * ChatHistory — /chat/history
 *
 * The dedicated, full-page view of every chat thread the user has ever
 * had with the assistant, grouped chronologically. The left sidebar in
 * the Chat workspace shows a short recent list for quick switching; this
 * page is the "see everything" view linked from the navbar and from that
 * sidebar's "View all chats" link.
 */
const ChatHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await getChatSessions();
        setSessions(res.data.sessions || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    sessions.forEach((s) => {
      const bucket = bucketFor(s.lastMessageAt);
      if (!groups[bucket]) groups[bucket] = [];
      groups[bucket].push(s);
    });
    return BUCKET_ORDER.filter((b) => groups[b]?.length).map((b) => ({ bucket: b, items: groups[b] }));
  }, [sessions]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    const prev = sessions;
    setSessions((s) => s.filter((sess) => sess._id !== id));
    try {
      await deleteChatSession(id);
    } catch {
      setSessions(prev);
    }
  };

  return (
    <main className="chat-history-page">
      <div className="chat-history-page__header">
        <h1 className="chat-history-page__title">Chat History</h1>
        <p className="chat-history-page__sub">Every conversation you've had with the MarginPro Assistant.</p>
      </div>

      {loading ? (
        <p className="chat-history-page__note">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="chat-history-page__note">No chats yet — start one from the Chat workspace.</p>
      ) : (
        grouped.map(({ bucket, items }) => (
          <section key={bucket} className="chat-history-group">
            <h2 className="chat-history-group__label">{bucket}</h2>
            <div className="chat-history-group__list">
              {items.map((s) => (
                <div key={s._id} className="chat-history-item glass-card" onClick={() => navigate(`/chat/${s._id}`)}>
                  <div className="chat-history-item__text">
                    <span className="chat-history-item__title">{s.title}</span>
                    {s.lastMessagePreview && (
                      <span className="chat-history-item__preview">{s.lastMessagePreview}</span>
                    )}
                  </div>
                  <div className="chat-history-item__meta">
                    <span className="chat-history-item__count">{s.messageCount} messages</span>
                    <button
                      className="chat-history-item__delete"
                      onClick={(e) => handleDelete(e, s._id)}
                      aria-label="Delete chat"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </main>
  );
};

export default ChatHistory;
